<template>
	<TnCardHorizontal
		v-if="(parsedProduct && !shoppingCartComponent) || (parsedProduct && !inCart && shoppingCartComponent)"
		dashed
		border
		shadow="s"
		:title="parsedProduct.cleanName"
		:text="`${price},-`"
	>
		<template #graphic>
			<NuxtImg
				:src="parsedProduct.imageLink"
				height="48px"
			/>
		</template>
		<template #actions>
			<TnButton
				size="xs"
				block
				@click="addToCart(parsedProduct)"
				@mouseenter="mouseOver = true"
				@mouseleave="mouseOver = false"
				:iconLeft="inCart ? (mouseOver ? 'close' : 'success') : ''"
				>{{ inCart ? (mouseOver ? "Fjern" : "Lagt til") : "Legg til" }}</TnButton
			>
		</template>
	</TnCardHorizontal>
</template>
<script>
import ecommerceIntegration from "~/integrations/ecommerce-v2-integration";
import ECommerceProductMixin from "~/components/ecommerce/ECommerceProductMixin/ECommerceProductMixin.js";
import { mapGetters, mapActions } from "vuex";
import ShoppingCartItemModel from "~/helpers/ecommerce/ShoppingCartItemModel/ShoppingCartItemModel.js";
import safeEcommerceUrl from "~/helpers/ecommerce/safeEcommerceUrl";
import GizmoComponentMixin from "~/mixins/platform/GizmoComponentMixin.js";
import { ProductCategory } from "~/components/ecommerce/types/ProductCategory";
export default defineNuxtComponent({
	name: "ECommerceRecommendedAccessory",

	mixins: [ECommerceProductMixin, GizmoComponentMixin],

	props: {
		shoppingCartComponent: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		return {
			recommendedAccessories: [],
			fetchAccessoryError: null,
			loadingInternally: false,
			mouseOver: false,
		};
	},

	computed: {
		...mapGetters("ecommerce", ["segment"]),
		...mapGetters("shoppingCart", ["visibleCartItems"]),
		...mapGetters("checkout", ["currentTerminal", "onlyAccessoryProducts"]),
		variantParent() {
			return this.eCommerceProduct.variantParentId;
		},
		rawAccessories() {
			return this.eCommerceProduct?.recommendedAccessories || [];
		},
		recommendedAccessoriesWithValidPrice() {
			return this.recommendedAccessories?.filter((product) => product?.price || product?.priceWithVat);
		},
		parsedProduct() {
			return this.recommendedAccessoriesWithValidPrice.filter((x) => x.inventory?.[0]?.amount > 0)[0];
		},
		price() {
			if (this.parsedProduct?.salePrice > 0) {
				if (this.segment === "business") {
					return Math.round(this.parsedProduct?.salePrice);
				}
				return Math.round(this.parsedProduct?.salePriceWithVat);
			} else if (this.parsedProduct) {
				if (this.segment === "business") {
					return Math.round(this.parsedProduct?.price);
				}
				return Math.round(this.parsedProduct?.priceWithVat);
			}

			return null;
		},
		inCart() {
			for (const item of this.visibleCartItems) {
				if (item.data?.id == this.parsedProduct?.id) {
					return item.id;
				}
				for (const subItem of item.subItems) {
					if (subItem.data?.id === this.parsedProduct?.id) {
						return subItem.id;
					}
				}
			}
			return false;
		},
	},

	methods: {
		...mapActions("ecommerce", ["setVariantAccessories"]),
		async loadAccessory() {
			try {
				this.loadingInternally = true;
				if (this.shoppingCartComponent) {
					if (this.currentTerminal) {
						const product = await ecommerceIntegration.getProductById(this.currentTerminal.data.id, this.segment);
						if (product.variantParentId) {
							this.recommendedAccessories = await ecommerceIntegration.getRecommendedAccessoriesForProduct(
								product.variantParentId,
								this.segment,
							);
						} else {
							this.recommendedAccessories = await ecommerceIntegration.getRecommendedAccessoriesForProduct(
								product.id,
								this.segment,
							);
						}
					}
				} else {
					if (this.variantParent) {
						// Fetch accessories connected to variant group
						this.recommendedAccessories = await ecommerceIntegration.getRecommendedAccessoriesForProduct(
							this.variantParent,
							this.segment,
						);
					} else if (this.rawAccessories.length > 0 && this.eCommerceProduct.id) {
						this.recommendedAccessories = await ecommerceIntegration.getRecommendedAccessoriesForProduct(
							this.eCommerceProduct.id,
							this.segment,
						);
					}
				}
			} catch (e) {
				this.fetchAccessoryError = e;
			} finally {
				this.loadingInternally = false;
			}
		},
		async addToCart(product) {
			if (this.inCart) {
				return await this.remove();
			}
			try {
				const metadata = {
					name: product?.title,
					prices: product?.prices,
					price: product?.priceWithVat,
					priceWithoutVat: product?.price,
					salePrice: product?.salePriceWithVat || undefined,
					salePriceWithoutVat: product?.salePrice || undefined,
					image: product?.imageLink,
					link: {
						to: safeEcommerceUrl(product?.category, product?.brand, product?.title, product?.condition),
					},
					category: ProductCategory.ACCESSORY,
				};
				const id = this.onlyAccessoryProducts ? undefined : this.currentTerminal?.id;
				await this.$store.dispatch(
					"shoppingCart/add",
					ShoppingCartItemModel(
						"hardware",
						{ id: product?.id, price: product?.priceWithVat, quantity: 1 },
						metadata,
						id,
					),
				);
				if (window.shoppingCart) {
					this.$store.commit("shoppingCart/open");
					window.shoppingCart.toggle();
				}
			} catch (err) {
				console.log(err);
			}
		},
		async remove() {
			try {
				await this.$store.dispatch("shoppingCart/remove", this.inCart);
			} catch (err) {
				console.error(err);
				this.error = err;
			}
		},
		mounted() {
			this.$emit("mounted", true);
		},
	},

	watch: {
		eCommerceProduct: {
			handler(value) {
				if (value?.id && !this.loadingInternally && !this.relatedProducts?.length && !this.fetchAccessoryError) {
					this.loadAccessory();
				}
			},
			immediate: true,
		},
	},
});
</script>
<style lang="scss" scoped></style>
